/**
 * Payment-method picker for the hotel checkout (M6). Lets the traveler choose a saved card or add a new
 * one (with a "save for future bookings" checkbox), and emits a `HotelPaymentSelection` the submit step
 * forwards to `SubmitHotelBooking`. Reuses the wallet store (saved cards) and the PCI Booking capture
 * iframe; the full card number never reaches Elara — only the opaque vault token plus safe metadata.
 *
 * Works the same for every rate, refundable or not: GDS hotel bookings pass the card to the property as
 * a guarantee/deposit and the hotel charges it card-on-file (no CVV) — so PAN + expiry, which replay from
 * a saved token, are all that's needed. No CVV is captured.
 *
 *  - Saved card selected → `{ kind: 'saved', paymentMethodId }`.
 *  - New card + "save"   → persisted to the wallet first, then `{ kind: 'saved', paymentMethodId }`.
 *  - New card, no save   → `{ kind: 'new', pciBookingToken, …metadata }` (one-off, not stored).
 */

import type { HotelPaymentSelection } from '@elara/domain/flows';

import type { CapturedCard } from '#views/wallet/CardCaptureFrame.jsx';

import { useState } from 'react';

import { usePaymentMethodsState } from '#states/hooks.js';
import { CardBrandIcon } from '#ui/CardBrandIcon.jsx';
import { Checkbox } from '#ui/checkbox.js';
import { Button, Loader } from '#ui/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { CardCaptureFrame } from '#views/wallet/CardCaptureFrame.jsx';

interface CheckoutCardSelectorProps {
  value: HotelPaymentSelection | undefined;
  onChange: (selection: HotelPaymentSelection | undefined) => void;
  /** The booking's check-out date — a card must be valid through it (the latest the hotel might charge). */
  bookingThrough?: Date;
}

/** End of a card's expiry month — a card is valid through the last day of that month. */
function cardExpiresBefore(expMonth: number, expYear: number, date: Date): boolean {
  return new Date(expYear, expMonth, 0, 23, 59, 59, 999).getTime() < date.getTime();
}

export function CheckoutCardSelector({ value, onChange, bookingThrough }: CheckoutCardSelectorProps) {
  const wallet = usePaymentMethodsState();
  const [adding, setAdding] = useState(false);
  const [saveNewCard, setSaveNewCard] = useState(true);
  /** Display metadata for a one-off (unsaved) card, since it isn't in the reactive wallet list. */
  const [oneOff, setOneOff] = useState<{ brand: string; last4: string } | null>(null);
  const [error, setError] = useState<string>();

  const cards = wallet.cards;
  // Show the capture frame on demand, or automatically when there are no saved cards to choose from.
  const showFrame = adding || (wallet.isReady && cards.length === 0);

  async function handleCaptured(card: CapturedCard) {
    setError(undefined);
    // A card that expires before the stay ends can't reliably guarantee the booking — reject it up front.
    if (bookingThrough && cardExpiresBefore(card.expMonth, card.expYear, bookingThrough)) {
      setError('That card expires before your stay — please use a card valid through your check-out date.');
      return;
    }
    if (saveNewCard) {
      // Persist first so the card resolves by id at booking time and appears in the wallet for reuse.
      const id = await wallet.addCard(card);
      setOneOff(null);
      onChange({ kind: 'saved', paymentMethodId: id });
    } else {
      setOneOff({ brand: card.brand, last4: card.last4 });
      onChange({ kind: 'new', ...card });
    }
    setAdding(false);
  }

  if (!wallet.isReady) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader loading />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {cards.length > 0 && (
        <div className="flex flex-col gap-2">
          {cards.map((card) => {
            const selected = value?.kind === 'saved' && value.paymentMethodId === card.id;
            const expired = card.isExpired;
            // Valid today but expires before the stay ends → the hotel may be unable to charge it.
            const expiresBeforeStay = !expired && bookingThrough != null && card.expiresBefore(bookingThrough);
            const unusable = expired || expiresBeforeStay;
            return (
              <button
                key={card.id}
                type="button"
                disabled={unusable}
                onClick={() => onChange({ kind: 'saved', paymentMethodId: card.id })}
                aria-pressed={selected}
                className={`flex items-center gap-4 px-4 py-3 rounded border cursor-pointer hover:border-input text-left transition-colors ${
                  unusable ? 'opacity-60 cursor-not-allowed' : selected && 'border-primary hover:border-primary'
                }`}
              >
                <CardBrandIcon brand={card.brand} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium flex items-center gap-2">
                    <span className="truncate text-sm">{card.displayLabel}</span>
                    {expired && (
                      <span className="text-xs rounded bg-destructive px-1.5 py-0.5 text-destructive-foreground">
                        Expired
                      </span>
                    )}
                    {expiresBeforeStay && (
                      <span className="text-xs rounded bg-destructive px-1.5 py-0.5 text-destructive-foreground">
                        Expires before stay
                      </span>
                    )}
                  </div>
                  <div className={`text-xs ${unusable ? 'text-destructive' : 'text-muted-foreground'}`}>
                    Expires {String(card.expMonth).padStart(2, '0')}/{card.expYear}
                  </div>
                </div>
                <MaterialIcon
                  icon={selected ? 'radio_button_checked' : 'radio_button_unchecked'}
                  className="text-primary"
                  size={IconSizes.sm}
                />
              </button>
            );
          })}
        </div>
      )}

      {/* A one-off (unsaved) card the traveler just entered — not in the wallet list, shown so it's selectable. */}
      {value?.kind === 'new' && oneOff && (
        <div className="flex items-center gap-4 px-4 py-3 rounded-md border border-primary ring-1 ring-primary bg-primary/5">
          <CardBrandIcon brand={oneOff.brand} />
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">
              {oneOff.brand} ···· {oneOff.last4}
            </div>
            <div className="text-sm text-muted-foreground">Won't be saved</div>
          </div>
          <MaterialIcon icon="check_circle" className="text-primary" size={IconSizes.sm} />
        </div>
      )}

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      {showFrame ? (
        <div className="flex flex-col gap-3">
          <CardCaptureFrame
            mintSession={async () => wallet.mintCaptureSession(window.location.origin)}
            onCaptured={handleCaptured}
            onCancel={cards.length > 0 ? () => setAdding(false) : undefined}
          />
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={saveNewCard} onCheckedChange={(checked) => setSaveNewCard(checked === true)} />
            Save this card for future bookings
          </label>
        </div>
      ) : (
        <Button variant="outline" className="self-start" onClick={() => setAdding(true)}>
          <MaterialIcon icon="add_card" /> Add a new card
        </Button>
      )}
    </div>
  );
}
